import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';
import { useCallback, useEffect, useState } from 'react';

export type MapStyleId = 'light' | 'streets' | 'satellite';

const OFM = 'https://tiles.openfreemap.org';
const STREETS_URL = (import.meta.env['VITE_MAP_STYLE_URL'] as string | undefined) ?? `${OFM}/styles/liberty`;
const LIGHT_URL = (import.meta.env['VITE_MAP_STYLE_LIGHT_URL'] as string | undefined) ?? `${OFM}/styles/positron`;
const DEFAULT_ID = ((import.meta.env['VITE_MAP_DEFAULT_STYLE'] as string | undefined) ?? 'light') as MapStyleId;
const STORAGE_KEY = 'rfleet.mapStyle';

export const MAP_STYLE_IDS: MapStyleId[] = ['light', 'streets', 'satellite'];

/** Fallback raster style (OSM) if a vector style is unreachable. */
export const OSM_RASTER: StyleSpecification = {
  version: 8,
  sources: { osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap contributors' } },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

/** Esri World Imagery; glyphs are needed so our own symbol layers (plates, place names) can render text. */
const SATELLITE_BASE: StyleSpecification = {
  version: 8,
  glyphs: `${OFM}/fonts/{fontstack}/{range}.pbf`,
  sources: {
    sat: {
      type: 'raster',
      tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
      tileSize: 256,
      maxzoom: 19,
      attribution: 'Esri, Maxar, Earthstar Geographics',
    },
  },
  layers: [{ id: 'sat', type: 'raster', source: 'sat' }],
};

let hybridCache: Promise<StyleSpecification> | null = null;

/** Satellite imagery + the streets style's label/shield layers on top (hybrid). Falls back to plain imagery. */
function satelliteHybrid(): Promise<StyleSpecification> {
  if (hybridCache) return hybridCache;
  hybridCache = fetch(STREETS_URL)
    .then((r) => (r.ok ? (r.json() as Promise<StyleSpecification>) : Promise.reject(new Error(String(r.status)))))
    .then((streets) => {
      const labels = streets.layers.filter((l): l is Extract<LayerSpecification, { type: 'symbol' }> => l.type === 'symbol');
      // Strong white halo so labels stay legible over imagery.
      const boosted = labels.map((l) => ({ ...l, paint: { ...(l.paint ?? {}), 'text-halo-color': '#ffffff', 'text-halo-width': 1.6, 'text-color': '#111827' } }));
      return { ...SATELLITE_BASE, sprite: streets.sprite, glyphs: streets.glyphs ?? SATELLITE_BASE.glyphs, sources: { ...streets.sources, ...SATELLITE_BASE.sources }, layers: [...SATELLITE_BASE.layers, ...boosted] } as StyleSpecification;
    })
    .catch(() => SATELLITE_BASE);
  return hybridCache;
}

/** Resolves a style id to something `new maplibregl.Map({ style })` accepts. */
export function resolveMapStyle(id: MapStyleId): Promise<string | StyleSpecification> {
  switch (id) {
    case 'streets':
      return Promise.resolve(STREETS_URL);
    case 'satellite':
      return satelliteHybrid();
    default:
      return Promise.resolve(LIGHT_URL);
  }
}

function readStored(): MapStyleId {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && (MAP_STYLE_IDS as string[]).includes(v)) return v as MapStyleId;
  } catch {
    /* ignore */
  }
  return (MAP_STYLE_IDS as string[]).includes(DEFAULT_ID) ? DEFAULT_ID : 'light';
}

const listeners = new Set<(id: MapStyleId) => void>();
let current: MapStyleId = readStored();

export function setMapStyle(id: MapStyleId): void {
  if (id === current) return;
  current = id;
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
  for (const l of listeners) l(id);
}

/** Shared (app-wide) basemap choice, persisted per browser. */
export function useMapStyle(): [MapStyleId, (id: MapStyleId) => void] {
  const [id, setId] = useState<MapStyleId>(current);
  useEffect(() => {
    listeners.add(setId);
    return () => {
      listeners.delete(setId);
    };
  }, []);
  const set = useCallback((next: MapStyleId) => setMapStyle(next), []);
  return [id, set];
}
